import React from 'react';
import { useTranslation } from 'react-i18next';
import { localeForI18n } from '../../utils/locale';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Card,
  CardBody,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  Spinner,
  Progress,
  Image,
  useColorModeValue,
  useToast,
  Flex,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import {
  Clock,
  CheckCircle,
  ChefHat,
  Utensils,
  Package,
  MapPin,
  Phone,
  User,
  RefreshCw,
  ArrowLeft,
  List,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { tableOrderingService } from '../../services/tableOrderingService';
import { formatCurrency } from '../../utils/currency';
import { updateOrderStatus, getOrderByNumber } from '../../utils/orderStorage';

interface OrderTrackingViewProps {
  orderNumber: string;
  guestPhone: string;
  onBackToMenu: () => void;
  onViewAllOrders?: () => void;
}

/**
 * OrderTrackingView Component
 * Shows order details and real-time status updates for guest orders
 */
const OrderTrackingView: React.FC<OrderTrackingViewProps> = ({
  orderNumber,
  guestPhone,
  onBackToMenu,
  onViewAllOrders
}) => {
  const { t, i18n } = useTranslation();
  const locale = localeForI18n(i18n.language);
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  // Fetch order details with polling for real-time updates
  const {
    data: order,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['orderTracking', orderNumber, guestPhone],
    queryFn: async () => {
      const response = await tableOrderingService.getOrderDetails(orderNumber, guestPhone);
      
      // Update cached order status in localStorage if different
      const cachedOrder = getOrderByNumber(orderNumber);
      if (cachedOrder && cachedOrder.status !== response.data.status) {
        updateOrderStatus(orderNumber, response.data.status, response.data);
      }
      
      return response.data;
    },
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: 45000, // Refresh every 45 seconds (was 15s)
    refetchIntervalInBackground: false, // Don't poll when tab is hidden
  });

  const getStatusInfo = (status: string) => {
    const meta: { [key: string]: { color: string; icon: any; descKey: string } } = {
      pending: { color: 'yellow', icon: Clock, descKey: 'pendingDesc' },
      confirmed: { color: 'blue', icon: CheckCircle, descKey: 'confirmedDesc' },
      preparing: { color: 'orange', icon: ChefHat, descKey: 'preparingDesc' },
      ready: {
        color: 'green',
        icon: Utensils,
        descKey: (order as any)?.order_type === 'pickup' ? 'readyDescPickup' : 'readyDescService'
      },
      served: { color: 'purple', icon: Package, descKey: 'servedDesc' },
      completed: { color: 'gray', icon: CheckCircle, descKey: 'completedDesc' },
      cancelled: { color: 'red', icon: Clock, descKey: 'cancelledDesc' }
    };
    const m = meta[status] || meta['pending'];
    const labelKey = (status === 'ready' ? 'readyLabel' : `${status === 'pending' ? 'pendingLabel' : status === 'confirmed' ? 'confirmedLabel' : status === 'preparing' ? 'preparingLabel' : status === 'served' ? 'servedLabel' : status === 'completed' ? 'completedLabel' : 'cancelledLabel'}`);
    return {
      label: t(`tableOrdering.tracking.statuses.${labelKey}`),
      color: m.color,
      icon: m.icon,
      description: t(`tableOrdering.tracking.statuses.${m.descKey}`)
    };
  };

  const getStatusProgress = (status: string): number => {
    const progressMap: { [key: string]: number } = {
      'pending': 20,
      'confirmed': 40,
      'preparing': 60,
      'ready': 80,
      'served': 90,
      'completed': 100,
      'cancelled': 0
    };
    return progressMap[status] || 0;
  };

  const formatOrderTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: t('tableOrdering.tracking.refreshed'),
      description: t('tableOrdering.tracking.refreshedDesc'),
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  if (isLoading) {
    return (
      <Container maxW="4xl" py={8}>
        <VStack spacing={6} align="center">
          <Spinner size="xl" color="blue.500" />
          <Text fontSize="lg">{t('tableOrdering.tracking.loading')}</Text>
        </VStack>
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container maxW="4xl" py={8}>
        <VStack spacing={6} align="center">
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">{t('tableOrdering.tracking.notFoundTitle')}</Text>
              <Text>{t('tableOrdering.tracking.notFoundBody', { number: orderNumber })}</Text>
            </Box>
          </Alert>
          <Button leftIcon={<Icon as={ArrowLeft} />} onClick={onBackToMenu}>
            {t('tableOrdering.tracking.backToMenu')}
          </Button>
        </VStack>
      </Container>
    );
  }

  const statusInfo = getStatusInfo(order.status);
  const progress = getStatusProgress(order.status);

  return (
    <Box bg={bgColor} minH="100vh">
      <Container maxW="4xl" py={6}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Flex justify="space-between" align="center">
            <Button 
              leftIcon={<Icon as={ArrowLeft} />} 
              variant="ghost" 
              onClick={onBackToMenu}
            >
              {t('tableOrdering.tracking.backToMenu')}
            </Button>
            <Button
              rightIcon={<Icon as={RefreshCw} />}
              variant="outline"
              onClick={handleRefresh}
              size="sm"
            >
              {t('tableOrdering.tracking.refresh')}
            </Button>
          </Flex>

          {/* Order Header */}
          <Card bg={cardBg} boxShadow="lg">
            <CardBody p={6}>
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={2}>
                    <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                      {t('tableOrdering.tracking.orderNumber', { number: order.order_number })}
                    </Text>
                    <Text color="gray.600" fontSize="sm">
                      {t('tableOrdering.tracking.placedOn', { time: formatOrderTime(order.created_at) })}
                    </Text>
                  </VStack>
                  <Badge 
                    colorScheme={statusInfo.color} 
                    size="lg" 
                    px={3} 
                    py={1} 
                    borderRadius="full"
                    textTransform="none"
                    fontSize="sm"
                  >
                    <HStack spacing={2}>
                      <Icon as={statusInfo.icon} boxSize={4} />
                      <Text>{statusInfo.label}</Text>
                    </HStack>
                  </Badge>
                </HStack>

                {/* Progress Bar */}
                <Box>
                  <Progress 
                    value={progress} 
                    colorScheme={statusInfo.color} 
                    size="lg" 
                    borderRadius="full"
                    bg="gray.100"
                  />
                  <Text mt={2} fontSize="sm" color="gray.600" textAlign="center">
                    {statusInfo.description}
                  </Text>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* Order Details Grid */}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
            {/* Restaurant & Table Info */}
            <GridItem>
              <Card bg={cardBg}>
                <CardBody>
                  <VStack spacing={4} align="start">
                    <Text fontSize="lg" fontWeight="semibold">{t('tableOrdering.tracking.restaurantDetails')}</Text>
                    <VStack spacing={3} align="start" w="full">
                      <HStack spacing={3}>
                        <Icon as={Utensils} color="blue.500" />
                        <Text fontWeight="medium">{order.restaurant_name}</Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon as={MapPin} color="blue.500" />
                        <Text>
                          {(order as any).order_type === 'pickup' ? t('tableOrdering.tracking.pickupOrder') : t('tableOrdering.tracking.tableLabel', { number: order.table_number })}
                        </Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon as={User} color="blue.500" />
                        <Text>{(order as any).guest_name || t('tableOrdering.tracking.guest')}</Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon as={Phone} color="blue.500" />
                        <Text>{(order as any).guest_phone || t('tableOrdering.tracking.na')}</Text>
                      </HStack>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>

            {/* Order Summary */}
            <GridItem>
              <Card bg={cardBg}>
                <CardBody>
                  <VStack spacing={4} align="start">
                    <Text fontSize="lg" fontWeight="semibold">{t('tableOrdering.tracking.summary')}</Text>
                    <VStack spacing={2} align="stretch" w="full">
                      <HStack justify="space-between">
                        <Text>{t('tableOrdering.tracking.orderType')}</Text>
                        <Badge colorScheme="blue">
                          {t(`tableOrdering.tracking.orderTypes.${(order as any).order_type || 'table_service'}`, { defaultValue: ((order as any).order_type || 'table_service').replace('_', ' ') })}
                        </Badge>
                      </HStack>
                      <HStack justify="space-between">
                        <Text>{t('tableOrdering.tracking.totalItems')}</Text>
                        <Text fontWeight="semibold">{order.items?.length || 0}</Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="lg" fontWeight="bold">{t('tableOrdering.tracking.totalAmount')}</Text>
                        <Text fontSize="lg" fontWeight="bold" color="blue.600">
                          {formatCurrency(Number(order.total_amount), (order as any).currency || 'USD')}
                        </Text>
                      </HStack>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>

          {/* Order Items */}
          <Card bg={cardBg}>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" fontWeight="semibold">{t('tableOrdering.tracking.items')}</Text>
                <Divider />
                <VStack spacing={3} align="stretch">
                  {order.items?.map((item, index) => (
                    <Card key={index} variant="outline">
                      <CardBody p={4}>
                        <Flex gap={4} align="center">
                          {(item as any).image_url && (
                            <Image
                              src={(item as any).image_url}
                              alt={(item as any).menu_item_name || t('tableOrdering.tracking.menuItemFallback')}
                              boxSize="60px"
                              objectFit="cover"
                              borderRadius="md"
                              flexShrink={0}
                            />
                          )}
                          <VStack align="start" flex="1" spacing={1}>
                            <Text fontWeight="semibold">{item.menu_item_name || t('tableOrdering.tracking.menuItemFallback')}</Text>
                            {item.special_instructions && (
                              <Text fontSize="sm" color="gray.600" fontStyle="italic">
                                {t('tableOrdering.tracking.noteLabel', { instructions: item.special_instructions })}
                              </Text>
                            )}
                            <HStack spacing={4}>
                              <Text fontSize="sm" color="gray.600">
                                {t('tableOrdering.tracking.qty', { quantity: item.quantity })}
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {t('tableOrdering.tracking.price', { price: formatCurrency(Number(item.unit_price), (order as any).currency || 'USD') })}
                              </Text>
                            </HStack>
                          </VStack>
                          <Text fontWeight="bold" color="blue.600">
                            {formatCurrency(Number(item.total_price), (order as any).currency || 'USD')}
                          </Text>
                        </Flex>
                      </CardBody>
                    </Card>
                  ))}
                </VStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Special Instructions */}
          {(order as any).special_requests && (
            <Card bg={cardBg}>
              <CardBody>
                <VStack spacing={3} align="start">
                  <Text fontSize="lg" fontWeight="semibold">{t('tableOrdering.tracking.specialInstructions')}</Text>
                  <Text color="gray.700">{(order as any).special_requests}</Text>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Action Buttons */}
          <HStack spacing={4} justify="center" pt={4} flexWrap="wrap">
            <Button 
              colorScheme="blue" 
              variant="outline" 
              onClick={onBackToMenu}
            >
              {t('tableOrdering.tracking.orderMore')}
            </Button>
            {onViewAllOrders && (
              <Button 
                variant="outline"
                leftIcon={<Icon as={List} />}
                onClick={onViewAllOrders}
              >
                {t('tableOrdering.tracking.viewAllOrders')}
              </Button>
            )}
            <Button 
              rightIcon={<Icon as={RefreshCw} />} 
              onClick={handleRefresh}
            >
              {t('tableOrdering.tracking.refreshStatus')}
            </Button>
          </HStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default OrderTrackingView;